import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AsyncBoundary, Card } from '@kollez/design-system';
import { useSkillTaxonomy, type SkillTaxonomyRow } from '@/lib/queries';

/**
 * /skills-catalog — public browser over the Kollez-owned SkillTaxonomy (Ship 5b).
 *
 * Six taxonomies live in the seed (skill, psychocognitive, realwork, language,
 * soft_skill, domain). Every Opportunity's requiredSkills[] and Badge's skill
 * field is a slug from here. Students, TPO, and companies all need a way to
 * see what canonical vocabulary exists — without this page, the only way to
 * discover a slug is to grep the seed data.
 */

const TAX_LABEL: Record<string, string> = {
  skill: 'Technical skills',
  psychocognitive: 'Psychocognitive',
  realwork: 'Real-work',
  language: 'Languages',
  soft_skill: 'Soft skills',
  domain: 'Domain',
};

const TAX_ORDER = ['skill', 'realwork', 'domain', 'soft_skill', 'psychocognitive', 'language'];

export function SkillsCatalogPage() {
  const q = useSkillTaxonomy();
  const [taxonomy, setTaxonomy] = useState<string>('');
  const [search, setSearch] = useState('');
  const rows = q.data?.rows ?? [];

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (taxonomy && r.taxonomy !== taxonomy) return false;
      if (!s) return true;
      if (r.slug.toLowerCase().includes(s)) return true;
      if (r.displayName.toLowerCase().includes(s)) return true;
      if (r.aliases.some((a) => a.toLowerCase().includes(s))) return true;
      return false;
    });
  }, [rows, taxonomy, search]);

  const byTaxonomy = useMemo(() => {
    const g: Record<string, SkillTaxonomyRow[]> = {};
    for (const r of filtered) (g[r.taxonomy] ??= []).push(r);
    return g;
  }, [filtered]);

  const countByTax = useMemo(() => {
    const g: Record<string, number> = {};
    for (const r of rows) g[r.taxonomy] = (g[r.taxonomy] ?? 0) + 1;
    return g;
  }, [rows]);

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">Skills catalog</p>
          <h1 className="h-page">Skill vocabulary</h1>
          <p className="subhead">
            The canonical skills Kollez uses to match candidates to opportunities and to issue
            badges. Every opening's required-skills list resolves to a slug here.
          </p>
        </div>
      </div>

      <Card padding="md">
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col text-sm flex-1 min-w-0">
            <span className="cell-sub">Search by name, slug, or alias</span>
            <input
              className="input mt-1"
              placeholder="e.g. python, cse, sales"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          <div className="flex flex-wrap gap-2 items-end">
            <button
              type="button"
              className={`tag ${!taxonomy ? 'tag-active' : ''}`}
              onClick={() => setTaxonomy('')}
              aria-pressed={!taxonomy}
            >
              All ({rows.length})
            </button>
            {TAX_ORDER.map((t) => (
              <button
                key={t}
                type="button"
                className={`tag ${taxonomy === t ? 'tag-active' : ''}`}
                onClick={() => setTaxonomy(taxonomy === t ? '' : t)}
                aria-pressed={taxonomy === t}
              >
                {TAX_LABEL[t] ?? t} ({countByTax[t] ?? 0})
              </button>
            ))}
          </div>
        </div>
      </Card>

      <AsyncBoundary
        kind="cards"
        loading={q.isLoading}
        error={q.error}
        empty={!q.isLoading && filtered.length === 0}
        emptyTitle={search || taxonomy ? 'No matching skills' : 'Catalog is empty'}
        emptyHint="Try a different search term or clear the taxonomy filter."
        onRetry={() => void q.refetch()}
      >
        <div className="stack">
          {TAX_ORDER.filter((t) => byTaxonomy[t]?.length).map((t) => (
            <section key={t}>
              <h2 className="h-section">
                {TAX_LABEL[t] ?? t}
                <span className="cell-sub ml-2">({byTaxonomy[t]!.length})</span>
              </h2>
              <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-3">
                {byTaxonomy[t]!.map((r) => (
                  <Link
                    key={r._id}
                    to={`/skills-catalog/${encodeURIComponent(r.slug)}`}
                    className="card card-pad hover:bg-secondary block"
                  >
                    <div className="font-medium truncate">{r.displayName}</div>
                    <div className="cell-sub mono truncate">{r.slug}</div>
                    {r.aliases.length > 0 && (
                      <div className="cell-sub truncate mt-1">
                        also: {r.aliases.slice(0, 3).join(', ')}
                        {r.aliases.length > 3 && ` +${r.aliases.length - 3}`}
                      </div>
                    )}
                  </Link>
                ))}
              </div>
            </section>
          ))}
        </div>
      </AsyncBoundary>
    </div>
  );
}
